/** File, microphone and tab-capture sources. */
import type { AudioSource } from './types';
import { SourceError } from './types';

export class FileSource implements AudioSource {
  readonly kind = 'file' as const;
  readonly monitoring = 'app' as const;
  readonly inputLatencyS = 0;
  readonly canPause = true;
  readonly label: string;
  readonly node: MediaElementAudioSourceNode;
  readonly element: HTMLAudioElement;
  readonly file: File;
  onEnded: (() => void) | null = null;
  private readonly url: string;

  constructor(ctx: AudioContext, file: File) {
    this.file = file;
    this.label = file.name;
    this.url = URL.createObjectURL(file);
    this.element = new Audio();
    this.element.src = this.url;
    this.element.preload = 'auto';
    this.element.addEventListener('ended', () => this.onEnded?.());
    this.node = ctx.createMediaElementSource(this.element);
  }

  get playing(): boolean {
    return !this.element.paused && !this.element.ended;
  }

  async start(): Promise<void> {
    try {
      await this.element.play();
    } catch (err) {
      throw new SourceError(
        String(err),
        `Could not play “${this.label}”. Is it an audio file this browser supports?`,
      );
    }
  }

  stop(): void {
    this.element.pause();
    this.element.removeAttribute('src');
    this.element.load();
    URL.revokeObjectURL(this.url);
  }

  togglePlay(): void {
    if (this.element.paused) void this.element.play();
    else this.element.pause();
  }

  seek(seconds: number): void {
    const d = this.element.duration;
    if (Number.isFinite(d)) this.element.currentTime = Math.max(0, Math.min(d - 0.05, seconds));
  }

  get position(): { time: number; duration: number } {
    const d = this.element.duration;
    return { time: this.element.currentTime, duration: Number.isFinite(d) ? d : 0 };
  }
}

/** Processing the browser applies for calls/meetings; all of it distorts music analysis. */
const RAW_AUDIO: MediaTrackConstraints = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
};

abstract class StreamSource implements AudioSource {
  abstract readonly kind: 'mic' | 'tab';
  abstract readonly monitoring: 'app' | 'external';
  readonly canPause = false;
  readonly playing = true;
  readonly node: MediaStreamAudioSourceNode;
  readonly label: string;
  readonly inputLatencyS: number;
  onEnded: (() => void) | null = null;

  protected constructor(
    ctx: AudioContext,
    protected readonly stream: MediaStream,
  ) {
    const track = stream.getAudioTracks()[0]!;
    this.label = track.label || 'Audio input';
    const settings = track.getSettings() as MediaTrackSettings & { latency?: number };
    this.inputLatencyS = typeof settings.latency === 'number' ? settings.latency : 0.01;
    this.node = ctx.createMediaStreamSource(stream);
    track.addEventListener('ended', () => this.onEnded?.());
  }

  start(): void {}

  stop(): void {
    for (const t of this.stream.getTracks()) t.stop();
  }
}

export class MicSource extends StreamSource {
  readonly kind = 'mic' as const;
  readonly monitoring = 'external' as const;

  /** Must be called from a user gesture. `deviceId` picks a specific input (or loopback device). */
  static async open(ctx: AudioContext, deviceId?: string): Promise<MicSource> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new SourceError('getUserMedia unavailable', 'This browser cannot access a microphone.');
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          ...RAW_AUDIO,
          channelCount: 2,
          ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        },
      });
      return new MicSource(ctx, stream);
    } catch (err) {
      throw new SourceError(
        String(err),
        'Microphone access was blocked or no microphone is connected.',
      );
    }
  }
}

export class TabSource extends StreamSource {
  readonly kind = 'tab' as const;
  readonly monitoring: 'app' | 'external';

  private constructor(ctx: AudioContext, stream: MediaStream) {
    super(ctx, stream);
    const settings = stream.getAudioTracks()[0]!.getSettings() as MediaTrackSettings & {
      suppressLocalAudioPlayback?: boolean;
    };
    // When the browser silences the captured tab, we play the audio ourselves (and can delay it).
    this.monitoring = settings.suppressLocalAudioPlayback ? 'app' : 'external';
  }

  /** Must be called from a user gesture. */
  static async open(ctx: AudioContext): Promise<TabSource> {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      throw new SourceError(
        'getDisplayMedia unavailable',
        'This browser cannot capture tab or system audio.',
      );
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: true, // most browsers require a video track; we stop it immediately
        audio: { ...RAW_AUDIO, suppressLocalAudioPlayback: true } as MediaTrackConstraints,
        systemAudio: 'include',
        selfBrowserSurface: 'exclude',
      } as DisplayMediaStreamOptions);
    } catch (err) {
      throw new SourceError(String(err), 'Sharing was cancelled or blocked.');
    }
    for (const v of stream.getVideoTracks()) v.stop();
    if (stream.getAudioTracks().length === 0) {
      throw new SourceError(
        'no audio track',
        'No audio was shared. Choose a tab (or screen) and turn on “Share audio”.',
      );
    }
    return new TabSource(ctx, stream);
  }
}

export interface AudioDevices {
  inputs: { id: string; label: string }[];
  outputs: { id: string; label: string }[];
}

/** Audio devices. Labels are only available after the user has granted microphone access once. */
export async function listAudioDevices(): Promise<AudioDevices> {
  const out: AudioDevices = { inputs: [], outputs: [] };
  if (!navigator.mediaDevices?.enumerateDevices) return out;
  const devices = await navigator.mediaDevices.enumerateDevices();
  devices.forEach((d, i) => {
    const entry = {
      id: d.deviceId,
      label: d.label || `${d.kind === 'audioinput' ? 'Input' : 'Output'} ${i + 1}`,
    };
    if (d.kind === 'audioinput') out.inputs.push(entry);
    if (d.kind === 'audiooutput') out.outputs.push(entry);
  });
  return out;
}
